import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Download, X, ChevronLeft, ChevronRight, AlertCircle } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { chatSupabase } from "@/integrations/supabase/client";

const _FAILED_MEDIA_INSTANCES_KEY = 'fluity_failed_media_instances';
function _loadFailedMediaInstances(): Set<string> {
  try {
    const stored = localStorage.getItem(_FAILED_MEDIA_INSTANCES_KEY);
    return stored ? new Set(JSON.parse(stored) as string[]) : new Set();
  } catch { return new Set(); }
}
function _saveFailedMediaInstances(set: Set<string>) {
  try { localStorage.setItem(_FAILED_MEDIA_INSTANCES_KEY, JSON.stringify([...set])); } catch {}
}
const _failedMediaInstances: Set<string> = _loadFailedMediaInstances();

interface MediaItem {
  id: string;
  messageId: string | null;
  fileName?: string;
  mediaUrl?: string | null;
  whatsappMessageId?: string | null; // id_message_channel (Evolution API ID)
}

const isWhatsAppCdnOrEnc = (url: string | null | undefined): boolean =>
  !!url && (url.includes('mmg.whatsapp.net') || url.includes('.enc'));

interface MediaViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageId: string | null; // message_id UUID (para navegar na galeria)
  whatsappMessageId?: string | null; // id_message_channel (para Evolution API)
  instanceId: string | null;
  phone: string | null;
  fileType: 'image' | 'video' | 'audio' | 'document';
  fileName?: string;
  mediaUrl?: string | null;
}

export function MediaViewer({
  open,
  onOpenChange,
  messageId,
  whatsappMessageId,
  instanceId,
  phone,
  fileType,
  fileName,
  mediaUrl: savedMediaUrl,
}: MediaViewerProps) {
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allImages, setAllImages] = useState<MediaItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadingGallery, setLoadingGallery] = useState(false);
  // Rastreia se a imagem atual foi carregada a partir de savedMediaUrl (evita override da galeria)
  const savedUrlLoadedRef = useRef(false);
  const userNavigatedRef = useRef(false);

  // Carregar imagem inicial quando o modal abrir — tem prioridade sobre a galeria
  useEffect(() => {
    if (open) {
      savedUrlLoadedRef.current = false;
      userNavigatedRef.current = false;
      if (savedMediaUrl && !isWhatsAppCdnOrEnc(savedMediaUrl)) {
        setMediaUrl(savedMediaUrl);
        setLoading(false);
        setError(null);
        savedUrlLoadedRef.current = true;
        return;
      }

      const waId = whatsappMessageId ?? messageId;
      if (waId && instanceId && phone) {
        fetchMedia(waId);
      } else {
        setError('Informações insuficientes para buscar o arquivo.');
        setLoading(false);
      }
    } else {
      setMediaUrl(null);
      setError(null);
      setAllImages([]);
      setCurrentIndex(0);
      savedUrlLoadedRef.current = false;
      userNavigatedRef.current = false;
    }
  }, [open, messageId, whatsappMessageId, instanceId, phone, savedMediaUrl, fileType]);

  // Buscar todas as imagens do chat quando o modal abrir (galeria de navegação)
  useEffect(() => {
    if (open && instanceId && phone && fileType === 'image') {
      fetchAllImages();
    }
  }, [open, instanceId, phone, fileType]);

  // Encontrar o índice da imagem atual na galeria
  useEffect(() => {
    if (allImages.length > 0 && messageId) {
      const index = allImages.findIndex(img => img.messageId === messageId);
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [allImages, messageId]);

  // Carregar a imagem ao navegar na galeria (apenas quando o usuário muda de índice explicitamente)
  useEffect(() => {
    if (!open || allImages.length === 0) return;
    // Não substituir a imagem carregada do savedMediaUrl a menos que o usuário navegue
    if (savedUrlLoadedRef.current && !userNavigatedRef.current) return;
    const currentImage = allImages[currentIndex];
    if (currentImage) {
      loadImage(currentImage);
    }
  }, [open, currentIndex, allImages]);

  const fetchAllImages = async () => {
    if (!instanceId || !phone) return;

    setLoadingGallery(true);
    try {
      const { data: messages, error: messagesError } = await chatSupabase
        .from('messages')
        .select('message_id, media_type, media_file_name, media_url, id_message_channel')
        .eq('instance_id', instanceId)
        .eq('origem_contato', phone)
        .or('media_type.ilike.image%,message_type.in.(image,imagemessage,imageMessage)')
        .not('media_url', 'is', null)
        .order('created_at', { ascending: true });

      if (messagesError) {
        console.error('Erro ao buscar imagens:', messagesError);
        return;
      }

      const images: MediaItem[] = (messages || [])
        .filter((msg: any) => msg.media_url)
        .map((msg: any) => ({
          id: msg.message_id,
          messageId: msg.message_id,
          fileName: msg.media_file_name,
          mediaUrl: msg.media_url,
          whatsappMessageId: msg.id_message_channel ?? msg.message_id,
        }));

      setAllImages(images);
    } catch (err) {
      console.error('Erro ao buscar imagens:', err);
    } finally {
      setLoadingGallery(false);
    }
  };

  const loadImage = async (image: MediaItem) => {
    setLoading(true);
    setError(null);

    if (image.mediaUrl && !isWhatsAppCdnOrEnc(image.mediaUrl)) {
      setMediaUrl(image.mediaUrl);
      setLoading(false);
      return;
    }

    // Se não tiver dados salvos (ou URL é WhatsApp CDN que falha no front), buscar via API
    if (image.whatsappMessageId && instanceId && phone) {
      await fetchMediaForImage(image.whatsappMessageId);
    } else {
      setError('Imagem não disponível');
      setLoading(false);
    }
  };

  const fetchMediaForImage = async (whatsappMsgId: string) => {
    if (!instanceId || !phone) return;

    if (_failedMediaInstances.has(instanceId)) {
      setError('Mídia não disponível');
      setLoading(false);
      return;
    }

    try {
      const evolutionApiUrl = import.meta.env.VITE_EVOLUTION_API_URL || 'https://api.fluity.space';
      const evolutionApiToken = import.meta.env.VITE_EVOLUTION_API_TOKEN || '77fbaccc54b75e38a55c2ec568ccd36e';

      const remoteJid = phone.includes('@') ? phone : `${phone}@s.whatsapp.net`;

      const endpoints = [
        {
          url: `${evolutionApiUrl}/chat/fetchMediaUrl/${instanceId}`,
          body: {
            messageKey: {
              remoteJid: remoteJid,
              id: whatsappMsgId,
              fromMe: true
            }
          }
        },
        {
          url: `${evolutionApiUrl}/chat/fetchMediaUrl/${instanceId}`,
          body: {
            messageId: whatsappMsgId,
            number: phone
          }
        }
      ];

      let response: Response | null = null;
      let got404 = false;

      for (const endpoint of endpoints) {
        try {
          const r = await fetch(endpoint.url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': evolutionApiToken,
            },
            body: JSON.stringify(endpoint.body)
          });

          if (r.ok) {
            response = r;
            break;
          }
          if (r.status === 404) got404 = true;
        } catch (err) {
          // network error, continue
        }
      }

      if (!response) {
        if (got404) {
          _failedMediaInstances.add(instanceId);
          _saveFailedMediaInstances(_failedMediaInstances);
        }
        setError('Mídia não disponível');
        return;
      }

      const data = await response.json();
      const url = data.mediaUrl || data.url || data.base64 || data.media?.url;

      if (url) {
        if (url.startsWith('data:') || url.includes('base64')) {
          setMediaUrl(url);
        } else if (url.startsWith('http://') || url.startsWith('https://')) {
          setMediaUrl(url);
        } else {
          const mimeType = getMimeType();
          setMediaUrl(`data:${mimeType};base64,${url}`);
        }
      } else {
        setError('URL da mídia não encontrada');
      }
    } catch (err) {
      console.error('Erro ao buscar mídia:', err);
      setError('Erro ao carregar arquivo');
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async (waMessageId?: string | null) => {
    const targetId = waMessageId ?? whatsappMessageId ?? messageId;
    if (!targetId || !instanceId || !phone) return;

    if (_failedMediaInstances.has(instanceId)) {
      setError('Mídia não disponível');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const evolutionApiUrl = import.meta.env.VITE_EVOLUTION_API_URL || 'https://api.fluity.space';
      const evolutionApiToken = import.meta.env.VITE_EVOLUTION_API_TOKEN || '77fbaccc54b75e38a55c2ec568ccd36e';

      const remoteJid = phone.includes('@') ? phone : `${phone}@s.whatsapp.net`;

      const endpoints = [
        {
          url: `${evolutionApiUrl}/chat/fetchMediaUrl/${instanceId}`,
          body: {
            messageKey: {
              remoteJid: remoteJid,
              id: targetId,
              fromMe: true
            }
          }
        },
        {
          url: `${evolutionApiUrl}/chat/fetchMediaUrl/${instanceId}`,
          body: {
            messageId: targetId,
            number: phone
          }
        }
      ];

      let response: Response | null = null;
      let got404 = false;

      for (const endpoint of endpoints) {
        try {
          const r = await fetch(endpoint.url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': evolutionApiToken,
            },
            body: JSON.stringify(endpoint.body)
          });

          if (r.ok) { response = r; break; }
          if (r.status === 404) got404 = true;
        } catch (err) {
          // network error, continue
        }
      }

      if (!response) {
        if (got404) {
          _failedMediaInstances.add(instanceId);
          _saveFailedMediaInstances(_failedMediaInstances);
        }
        setError('Não foi possível buscar o arquivo');
        return;
      }

      const data = await response.json();
      const url = data.mediaUrl || data.url || data.base64 || data.media?.url;

      if (url) {
        if (url.startsWith('data:') || url.includes('base64')) {
          setMediaUrl(url);
        } else if (url.startsWith('http://') || url.startsWith('https://')) {
          setMediaUrl(url);
        } else {
          const mimeType = getMimeType();
          setMediaUrl(`data:${mimeType};base64,${url}`);
        }
      } else {
        setError('URL da mídia não encontrada');
      }
    } catch (err) {
      console.error('Erro ao buscar mídia:', err);
      setError('Erro ao carregar arquivo');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!mediaUrl) return;
    
    const link = document.createElement('a');
    link.href = mediaUrl;
    link.download = allImages[currentIndex]?.fileName || fileName || `imagem.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getMimeType = (file?: string | null) => {
    const fileName = file || allImages[currentIndex]?.fileName;
    if (fileName) {
      const ext = fileName.toLowerCase().split('.').pop();
      const mimeTypes: Record<string, string> = {
        'jpg': 'image/jpeg', 'jpeg': 'image/jpeg', 'png': 'image/png',
        'gif': 'image/gif', 'webp': 'image/webp', 'svg': 'image/svg+xml',
      };
      return mimeTypes[ext || ''] || 'image/jpeg';
    }
    return 'image/jpeg';
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      userNavigatedRef.current = true;
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < allImages.length - 1) {
      userNavigatedRef.current = true;
      setCurrentIndex(currentIndex + 1);
    }
  };

  const getThumbnailUrl = (image: MediaItem): string | null => {
    return image.mediaUrl || null;
  };

  const currentImage = allImages[currentIndex] || null;
  const currentFileName = currentImage?.fileName || fileName || 'Imagem';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[100vw] w-full max-h-[100vh] h-full p-0 flex flex-col bg-black/95 backdrop-blur-md border-white/10 rounded-none">
        <VisuallyHidden.Root>
          <DialogTitle>{currentFileName}</DialogTitle>
        </VisuallyHidden.Root>
        {fileType === 'image' ? (
          <div className="relative w-full h-full flex flex-col min-h-0 overflow-hidden">
            {/* Header com ações */}
            <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent">
              <div className="flex flex-col">
                <span className="text-white font-medium text-sm truncate max-w-[200px] sm:max-w-md">
                  {currentFileName}
                </span>
                {allImages.length > 1 && (
                  <span className="text-white/60 text-[10px]">
                    {currentIndex + 1} de {allImages.length}
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                {mediaUrl && (
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={handleDownload} 
                    className="text-white hover:bg-white/20 h-9 w-9 rounded-full"
                    title="Baixar imagem"
                  >
                    <Download className="h-5 w-5" />
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => onOpenChange(false)}
                  className="text-white hover:bg-white/20 h-9 w-9 rounded-full"
                  title="Fechar"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
            </div>

            {/* Setas de navegação */}
            {allImages.length > 1 && (
              <>
                {currentIndex > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handlePrevious}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20 h-12 w-12 rounded-full bg-black/40 backdrop-blur-sm"
                    title="Imagem anterior"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>
                )}
                {currentIndex < allImages.length - 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleNext}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20 h-12 w-12 rounded-full bg-black/40 backdrop-blur-sm"
                    title="Próxima imagem"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </Button>
                )}
              </>
            )}

            {/* Container da imagem central */}
            <div className="flex-1 flex items-center justify-center p-4 bg-black/20 overflow-hidden" style={{ paddingBottom: allImages.length > 1 ? '120px' : '1rem' }}>
              {loading && (
                <div className="flex flex-col items-center gap-4 animate-in fade-in duration-500">
                  <div className="animate-spin rounded-full h-12 w-12 border-2 border-white/20 border-t-white"></div>
                  <p className="text-sm text-white/70 font-medium">Carregando imagem...</p>
                </div>
              )}
              
              {error && (
                <div className="flex flex-col items-center gap-4 text-center max-w-sm px-6 py-8 bg-black/40 rounded-2xl border border-white/10">
                  <AlertCircle className="h-10 w-10 text-red-400" />
                  <div className="text-white font-medium">{error}</div>
                </div>
              )}
              
              {mediaUrl && !loading && !error && (
                <div className="relative w-full h-full flex items-center justify-center animate-in zoom-in-95 duration-300 ease-out">
                  <img 
                    src={mediaUrl} 
                    alt={currentFileName} 
                    className="max-w-[70vw] max-w-[500px] max-h-[60vh] max-h-[500px] w-auto h-auto object-contain select-none shadow-[0_0_50px_rgba(0,0,0,0.5)] rounded-lg"
                    onContextMenu={(e) => e.preventDefault()}
                    style={{ 
                      maxWidth: 'min(70vw, 500px)',
                      maxHeight: 'min(60vh, 500px)'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Galeria de thumbnails na parte inferior */}
            {allImages.length > 1 && (
              <div className="absolute bottom-0 left-0 right-0 z-40 bg-gradient-to-t from-black/90 to-transparent p-4">
                <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
                  {allImages.map((image, index) => {
                    const thumbnailUrl = getThumbnailUrl(image);
                    const isActive = index === currentIndex;
                    
                    return (
                      <button
                        key={image.id}
                        onClick={() => { userNavigatedRef.current = true; setCurrentIndex(index); }}
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                          isActive 
                            ? 'border-green-500 scale-105 shadow-lg shadow-green-500/50' 
                            : 'border-white/20 hover:border-white/40'
                        }`}
                      >
                        {thumbnailUrl ? (
                          <img 
                            src={thumbnailUrl} 
                            alt={image.fileName || `Imagem ${index + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-white/20 border-t-white"></div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          // Visualizador padrão para outros tipos (mantém código original simplificado)
          <div className="flex-1 p-4 md:p-6 flex items-center justify-center min-h-0 bg-muted/30 overflow-hidden">
            {loading && (
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="text-sm text-muted-foreground">Carregando arquivo...</p>
              </div>
            )}
            
            {error && (
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="text-destructive">{error}</div>
              </div>
            )}
            
            {mediaUrl && !loading && !error && (
              <div className="w-full h-full flex items-center justify-center min-w-0 min-h-0">
                {fileType === 'video' && (
                  <video src={mediaUrl} controls className="max-w-full max-h-full rounded-lg">
                    Seu navegador não suporta vídeo.
                  </video>
                )}
                
                {fileType === 'audio' && (
                  <div className="w-full max-w-md">
                    <audio src={mediaUrl} controls className="w-full">
                      Seu navegador não suporta áudio.
                    </audio>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
